import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'react-bootstrap-5';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuildoutLogo } from 'helpers/custom_fontawesome_icons';

import ResultCard from './results/result_card';
import Paginator from './results/paginator';
import CriteriaDecorator from '../helpers/criteria_decorator';
import { showSaveSearchModal } from 'bundles/connect/components/store/actions/ui';
import pluralize from 'pluralize';
import { EmptyState } from './results/empty_state';
import EmptyStateBadges from './results/empty_state_badges';
import { selectCriteria } from '../../store/selectors/criteria';
import { selectDetailedResults, selectTotal } from '../../store/selectors/results';
import { selectLoading, selectOffMarketSearch } from '../../store/selectors/ui';

type ResultsProps = {
  containerRef: React.RefObject<HTMLDivElement>;
}

export default function Results({
  containerRef
}: ResultsProps) {
  const dispatch = useDispatch();
  const criteria = useSelector(selectCriteria);
  const detailedResults = useSelector(selectDetailedResults);
  const loading = useSelector(selectLoading);
  const offMarketSearch = useSelector(selectOffMarketSearch);
  const total = useSelector(selectTotal);

  const decoratedCriteria = new CriteriaDecorator(criteria, offMarketSearch);
  const filterCount = decoratedCriteria.numCriteriaExcludingLocation();

  if (!detailedResults) {
    return (
      <div className="text-center py-4">
        <FontAwesomeIcon beatFade className="text-primary" icon={faBuildoutLogo} size="3x" />
      </div>
    );
  }

  if (detailedResults.length === 0) {
    const openSaveSearchModal = (e) => {
      e.preventDefault();
      dispatch(showSaveSearchModal(true));
    };

    return (
      <div>
        <EmptyState subtitle="Expand your search to find matching listings" title="No matches" />
        <EmptyStateBadges />
        <div className="px-4">
          <Row className="my-6 align-items-center">
            <Col><hr className="m-0" /></Col>
            <Col xs="auto"><h5 className="text-muted m-0">OR</h5></Col>
            <Col><hr className="m-0" /></Col>
          </Row>
        </div>
        <p className="px-md-5 text-center">
          <a href="#" onClick={openSaveSearchModal}>
            <strong className="text-primary">Save this search</strong>
          </a>
          {' '}to get notified when exact matches are added to Buildout
        </p>
      </div>
    );
  }

  const showTotal = total && filterCount > 0;
  return (
    <div className="h-100 d-flex flex-column">
      {showTotal && (<div className="mt-3 px-3 flex-grow-0">
        <small className="fw-semibold">{total >= 1000 ? '1,000+' : total} {pluralize('listing', total)}</small>
      </div>)}
      <div className={`p-3 h-100 overflow-auto transition-opacity ${loading ? 'opacity-75' : 'opacity-100'}`}
        ref={containerRef}
      >
        {detailedResults.map(result => (
          <ResultCard key={result.saleListingId} result={result} />
        ))}
      </div>

      <div className="d-flex justify-content-center">
        <Paginator containerRef={containerRef} />
      </div>
    </div>
  );
}
